import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Link,
  MenuItem,
  Paper,
  Select as MuiSelect,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Toast from "../../components/Toast";
import taskerAuth from "../../services/taskerAuth";
import { readErrorMessage } from "./utils/mapBeErrors";
import { maskMobile } from "./utils/formatting";
import { COUNTRY_CODES } from "./fields/PhoneField";
import { PHONE_RULES, validatePhone } from "./utils/schemaUtils";

type Stage = "mobile" | "otp";

const DEFAULT_MAX_LENGTH = 15;
const OTP_LEN = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const GENERIC_SEND_OTP_ERROR = "We couldn't send an OTP right now. Please try again in a moment.";

type ToastItem = {
  id: string;
  text: string;
  type: "success" | "error";
  autoClose: boolean;
};

function validateMobile(mobile: string, countryCode: string): string | null {
  if (!mobile) return "Enter your mobile number.";
  const phoneErr = validatePhone(mobile, countryCode);
  if (phoneErr) return phoneErr;
  return null;
}

function VendorOnboardingLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  const [stage, setStage] = useState<Stage>("mobile");
  const [countryCode, setCountryCode] = useState("+91");
  const [mobile, setMobile] = useState("");
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string>("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toastList, setToastList] = useState<ToastItem[]>([]);
  const [resendCooldown, setResendCooldown] = useState(0);

  const mobileMaxLength = PHONE_RULES[countryCode]?.length ?? DEFAULT_MAX_LENGTH;

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const pushToast = (text: string, type: "success" | "error") => {
    setToastList((list) => [
      ...list,
      {
        id: `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        text,
        type,
        autoClose: true,
      },
    ]);
  };

  const handleCountryCodeChange = (nextCode: string) => {
    setCountryCode(nextCode);
    const nextMax = PHONE_RULES[nextCode]?.length ?? DEFAULT_MAX_LENGTH;
    setMobile((prev) => prev.slice(0, nextMax));
    if (mobileError) setMobileError(null);
  };

  const handleMobileChange = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, mobileMaxLength);
    setMobile(cleaned);
    if (mobileError) setMobileError(null);
  };

  const handleOtpChange = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, OTP_LEN);
    setOtp(cleaned);
    if (otpError) setOtpError(null);
  };

  const handleSendOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validateMobile(mobile, countryCode);
    if (validationError) {
      setMobileError(validationError);
      return;
    }
    setMobileError(null);
    setSubmitting(true);
    try {
      const response = await taskerAuth.sendOtp(mobile, countryCode);
      setRequestId(response?.request_id ?? "");
      setOtp("");
      setOtpError(null);
      setStage("otp");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      pushToast(GENERIC_SEND_OTP_ERROR, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (otp.length !== OTP_LEN) {
      setOtpError(`Enter the ${OTP_LEN}-digit OTP.`);
      return;
    }
    setOtpError(null);
    setSubmitting(true);
    try {
      const response = await taskerAuth.verifyOtp(
        requestId,
        otp,
        mobile,
        countryCode,
      );
      if (!response?.access_token) {
        pushToast("The OTP you entered is invalid or expired.", "error");
        return;
      }
      navigate("/vendor-onboarding" + location.search, { replace: true });
    } catch (err) {
      pushToast(
        readErrorMessage(err, "The OTP you entered is invalid or expired."),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setOtp("");
    setOtpError(null);
    setSubmitting(true);
    try {
      const response = await taskerAuth.sendOtp(mobile, countryCode);
      setRequestId(response?.request_id ?? "");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      pushToast(GENERIC_SEND_OTP_ERROR, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeMobile = () => {
    setStage("mobile");
    setOtp("");
    setOtpError(null);
    setRequestId("");
  };

  return (
    <>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        minHeight: "calc(100vh - 130px)",
        px: 2,
        py: 6,
        boxSizing: "border-box",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 440,
          p: { xs: 3, sm: 5 },
          border: 1,
          borderColor: "#E0E4EF",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)",
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 22,
            }}
          >
            V
          </Box>
        </Box>

        <Typography
          sx={{ fontSize: 24, fontWeight: 700, mb: 0.5, textAlign: "center" }}
        >
          Sign in
        </Typography>

        {stage === "mobile" ? (
          <>
            <Typography
              variant="body2"
              sx={{ mb: 4, color: "text.secondary", textAlign: "center" }}
            >
              Enter your registered mobile number.
            </Typography>
            <Box
              component="form"
              onSubmit={handleSendOtp}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <MuiSelect
                    value={countryCode}
                    onChange={(e) =>
                      handleCountryCodeChange(e.target.value as string)
                    }
                    disabled={submitting}
                    size="small"
                    variant="outlined"
                    inputProps={{ "aria-label": "Country code" }}
                    sx={{ width: 120, flexShrink: 0 }}
                  >
                    {COUNTRY_CODES.map((cc) => (
                      <MenuItem key={cc.code} value={cc.code}>
                        {cc.label}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                  <TextField
                    label="Mobile number"
                    type="tel"
                    value={mobile}
                    onChange={(e) => handleMobileChange(e.target.value)}
                    error={Boolean(mobileError)}
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: mobileMaxLength,
                      "aria-label": "Mobile number",
                    }}
                    autoFocus
                    fullWidth
                    size="small"
                    disabled={submitting}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: mobileError ? "error.main" : "text.secondary",
                    mt: 0.5,
                    ml: 1.5,
                    display: "block",
                    minHeight: "1.25em",
                  }}
                >
                  {mobileError || " "}
                </Typography>
              </Box>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                fullWidth
                size="large"
                sx={{ mt: 1, py: 1.25, fontWeight: 600 }}
              >
                {submitting ? "Sending OTP…" : "Send OTP"}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{ mb: 4, color: "text.secondary", textAlign: "center" }}
            >
              We sent a {OTP_LEN}-digit OTP to {countryCode}{" "}
              {maskMobile(mobile)}.
            </Typography>
            <Box
              component="form"
              onSubmit={handleVerifyOtp}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <TextField
                label="Enter OTP"
                value={otp}
                size="small"
                onChange={(e) => handleOtpChange(e.target.value)}
                error={Boolean(otpError)}
                helperText={otpError || " "}
                inputProps={{
                  inputMode: "numeric",
                  maxLength: OTP_LEN,
                  autoComplete: "one-time-code",
                  "aria-label": "Enter OTP",
                }}
                autoFocus
                fullWidth
                disabled={submitting}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                fullWidth
                size="large"
                sx={{ mt: 1, py: 1.25, fontWeight: 600 }}
              >
                {submitting ? "Verifying…" : "Verify & Continue"}
              </Button>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Didn&apos;t get it?{" "}
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleResend}
                    disabled={submitting || resendCooldown > 0}
                  >
                    {resendCooldown > 0
                      ? `Resend OTP in ${resendCooldown}s`
                      : "Resend OTP"}
                  </Link>
                </Typography>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleChangeMobile}
                  disabled={submitting}
                >
                  Change mobile
                </Link>
              </Stack>
            </Box>
          </>
        )}
      </Paper>
    </Box>
    <Toast toastList={toastList} setToastList={setToastList} />
    </>
  );
}

export default VendorOnboardingLogin;
